/**
 * Admin dialog — manage a program's category (Diploma/Certificate) and its
 * cohort start/end dates per admission term. A program can have multiple
 * entries inside the same term.
 */
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Plus, Trash2, CalendarDays, Loader2 } from "lucide-react";

const TERMS = [
    "Term 1 (January – April)",
    "Term 2 (May – August)",
    "Term 3 (September – December)",
];

function fmtError(e, fb) { return e?.response?.data?.detail || e?.message || fb; }

export default function ProgramScheduleDialog({ program, onClose }) {
    const [credential, setCredential] = useState("");
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const programId = program?.id;
    const open = !!programId;

    const load = useCallback(async () => {
        if (!programId) return;
        setLoading(true);
        try {
            setCredential(program?.credential_type || "");
            const { data } = await api.get(`/admin/programs/${programId}/schedule`);
            setEntries((data.items || []).map((row) => ({
                term: row.term,
                start_date: row.start_date,
                end_date: row.end_date,
            })));
        } catch (e) {
            toast.error(fmtError(e, "Could not load schedule"));
        } finally {
            setLoading(false);
        }
    }, [programId, program]);

    useEffect(() => { if (open) load(); }, [open, load]);

    const addEntry = () => {
        setEntries((e) => [...e, { term: TERMS[0], start_date: "", end_date: "" }]);
    };
    const updateEntry = (idx, key, val) => {
        setEntries((es) => es.map((e, i) => i === idx ? { ...e, [key]: val } : e));
    };
    const removeEntry = (idx) => {
        setEntries((es) => es.filter((_, i) => i !== idx));
    };

    const save = async () => {
        if (!programId) return;
        for (const e of entries) {
            if (!e.term || !e.start_date || !e.end_date) {
                toast.error("Every row needs a term and start + end dates.");
                return;
            }
            if (e.start_date >= e.end_date) {
                toast.error(`${e.term}: end date must be after start date.`);
                return;
            }
        }
        setSaving(true);
        try {
            if (credential && credential !== program?.credential_type) {
                await api.patch(`/admin/programs/${programId}`, { credential_type: credential });
            }
            await api.put(`/admin/programs/${programId}/schedule`, entries);
            toast.success("Schedule saved.");
            onClose?.(true);
        } catch (e) {
            toast.error(fmtError(e, "Could not save schedule"));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
            <DialogContent className="max-w-2xl" data-testid="program-schedule-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display flex items-center gap-2">
                        <CalendarDays className="h-4 w-4 text-[#C79520]" />
                        Schedule — {program?.title}
                    </DialogTitle>
                </DialogHeader>

                <div className="space-y-4">
                    <div>
                        <Label>Credential category</Label>
                        <Select value={credential} onValueChange={setCredential}>
                            <SelectTrigger data-testid="program-credential-select">
                                <SelectValue placeholder="Choose…" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="diploma">Diploma Program</SelectItem>
                                <SelectItem value="certificate">Certificate Program</SelectItem>
                            </SelectContent>
                        </Select>
                        <p className="text-[11px] text-muted-foreground mt-1">
                            Determines where this program appears in the public sign-up form.
                        </p>
                    </div>

                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <Label>Cohort dates</Label>
                            <Badge variant="outline">{entries.length} cohort{entries.length === 1 ? "" : "s"}</Badge>
                        </div>
                        {loading ? (
                            <div className="flex items-center justify-center py-6">
                                <Loader2 className="h-5 w-5 animate-spin text-[#134B30]" />
                            </div>
                        ) : entries.length === 0 ? (
                            <div className="text-xs text-muted-foreground italic py-3">
                                No cohort dates yet. Add the program's start/end dates per term.
                            </div>
                        ) : (
                            <div className="space-y-2">
                                {entries.map((e, idx) => (
                                    <div key={idx} className="grid grid-cols-12 gap-2 items-end" data-testid={`schedule-entry-${idx}`}>
                                        <div className="col-span-5">
                                            <Label className="text-[10px]">Term</Label>
                                            <Select value={e.term} onValueChange={(v) => updateEntry(idx, "term", v)}>
                                                <SelectTrigger data-testid={`schedule-term-${idx}`}>
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {TERMS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div className="col-span-3">
                                            <Label className="text-[10px]">Start</Label>
                                            <Input
                                                type="date" value={e.start_date}
                                                onChange={(ev) => updateEntry(idx, "start_date", ev.target.value)}
                                                data-testid={`schedule-start-${idx}`}
                                            />
                                        </div>
                                        <div className="col-span-3">
                                            <Label className="text-[10px]">End</Label>
                                            <Input
                                                type="date" value={e.end_date}
                                                onChange={(ev) => updateEntry(idx, "end_date", ev.target.value)}
                                                data-testid={`schedule-end-${idx}`}
                                            />
                                        </div>
                                        <div className="col-span-1">
                                            <Button
                                                size="sm" variant="ghost"
                                                onClick={() => removeEntry(idx)}
                                                className="text-rose-600 hover:bg-rose-50"
                                                data-testid={`schedule-remove-${idx}`}
                                            >
                                                <Trash2 className="h-3.5 w-3.5" />
                                            </Button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                        <Button
                            variant="outline" size="sm" onClick={addEntry}
                            className="mt-3 gap-1.5"
                            data-testid="add-schedule-entry"
                        >
                            <Plus className="h-3.5 w-3.5" />
                            Add cohort date
                        </Button>
                    </div>
                </div>

                <DialogFooter>
                    <Button variant="ghost" onClick={() => onClose?.()}>Cancel</Button>
                    <Button
                        onClick={save} disabled={saving}
                        className="bg-[#134B30] hover:bg-[#0F3D26] text-white gap-1.5"
                        data-testid="save-schedule-btn"
                    >
                        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CalendarDays className="h-4 w-4" />}
                        Save schedule
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
